import { useEffect } from "react";
import { Button, FloatingLabel, Form, Offcanvas } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import WebServiceAmazon from "../../../Services/WebServiceAmazon";
import { Label } from "../../Common/Label/Label";

interface Props {
  isShow: boolean;
  onClose: () => void;
  onSaved: () => void;
  campaignId: string;
  campaignType?: string;
}

const campaignTypeToAdGroupType = (campaignType?: string) => {
  switch (campaignType) {
    case "SP_CAMPAIGN":
      return "SP_ADGROUP";
    case "SD_CAMPAIGN":
      return "SD_ADGROUP";
    case "SB_CAMPAIGN":
      return "SB_ADGROUP";
    default:
      return "";
  }
};

const AddAdGroupDrawer = (props: Props) => {
  const profileData = useSelector((state: any) => state.commonReducer.data) || null;
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm({ mode: "onChange" });
  const watchAllFields = watch();

  useEffect(() => {
    if (props.isShow) {
      reset({ name: "", defaultBid: "", state: "ENABLED" });
    }
  }, [props.isShow]);

  const onSubmit = (data: any) => {
    const adGroupType = campaignTypeToAdGroupType(props.campaignType);
    if (!adGroupType) {
      toast.error("Unsupported campaign type for ad group creation.");
      return;
    }
    if (!props.campaignId) {
      toast.error("Campaign ID is missing.");
      return;
    }

    const adGroup: any = {
      name: data.name?.trim(),
      campaignId: props.campaignId,
      state: data.state,
    };
    if (adGroupType !== "SB_ADGROUP") {
      adGroup.defaultBid = Number(data.defaultBid);
    }

    const body = { adGroups: [adGroup] };

    const query = new URLSearchParams({
      profileId: profileData?.value ?? "",
      type: adGroupType,
    }).toString();

    WebServiceAmazon.addLoader("add-adgroup-btn");
    WebServiceAmazon.postAPI({
      action: `amazon/ad-group/add?${query}`,
      body,
    })
      .then((res: any) => {
        WebServiceAmazon.removeLoader("add-adgroup-btn");
        const created = res?.adGroups;
        if (created?.error?.length > 0) {
          const err = created.error[0];
          toast.error(err?.errors?.[0]?.errorValue?.message || err?.message || "Failed to create ad group");
          return;
        }
        toast.success(res?.message || "Ad group created successfully");
        props.onSaved();
        props.onClose();
      })
      .catch((error: any) => {
        WebServiceAmazon.removeLoader("add-adgroup-btn");
        toast.error(error?.message || "Failed to create ad group");
      });
  };

  const isSB = campaignTypeToAdGroupType(props.campaignType) === "SB_ADGROUP";

  return (
    <Offcanvas
      show={props.isShow}
      onHide={props.onClose}
      placement="end"
      className="righSide-modal size-md"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Create Ad Group</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="wmx-normal-form">
          <div className="wmx-form-style">
            <div className="mb-3">
              <Controller
                control={control}
                name="name"
                rules={{
                  required: "Ad group name is required",
                  maxLength: { value: 255, message: "Max 255 characters" },
                }}
                render={({ field: { onChange } }) => (
                  <>
                    <FloatingLabel controlId="adGroupName" label="Ad Group Name">
                      <Form.Control
                        type="text"
                        placeholder="Ad Group Name"
                        value={watchAllFields.name || ""}
                        onChange={onChange}
                      />
                    </FloatingLabel>
                    {errors.name?.message && (
                      <Label
                        title={errors.name?.message?.toString()}
                        modeError={true}
                      />
                    )}
                  </>
                )}
              />
            </div>

            {!isSB && (
              <div className="mb-3">
                <Controller
                  control={control}
                  name="defaultBid"
                  rules={{
                    required: "Default bid is required",
                    validate: (v: any) =>
                      (!isNaN(Number(v)) && Number(v) > 0) ||
                      "Default bid must be a positive number",
                  }}
                  render={({ field: { onChange } }) => (
                    <>
                      <FloatingLabel controlId="defaultBid" label="Default Bid ($)">
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0.02"
                          placeholder="Default Bid"
                          value={watchAllFields.defaultBid ?? ""}
                          onChange={onChange}
                        />
                      </FloatingLabel>
                      {errors.defaultBid?.message && (
                        <Label
                          title={errors.defaultBid?.message?.toString()}
                          modeError={true}
                        />
                      )}
                    </>
                  )}
                />
              </div>
            )}

            <div className="mb-3">
              <Controller
                control={control}
                name="state"
                rules={{ required: "Please select a status" }}
                render={({ field: { onChange } }) => (
                  <>
                    <FloatingLabel controlId="adGroupState" label="Status">
                      <Form.Select
                        onChange={onChange}
                        value={watchAllFields.state || "ENABLED"}
                      >
                        <option value="ENABLED">Enabled</option>
                        <option value="PAUSED">Paused</option>
                      </Form.Select>
                    </FloatingLabel>
                    {errors.state?.message && (
                      <Label
                        title={errors.state?.message?.toString()}
                        modeError={true}
                      />
                    )}
                  </>
                )}
              />
            </div>
          </div>
        </Offcanvas.Body>
        <div className="offcanvas-footer">
          <Button
            variant="secondary"
            className="btn btn-wmx-gray"
            type="button"
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button
            id="add-adgroup-btn"
            className="btn btn-wmx loader-btn"
            type="submit"
          >
            Create
          </Button>
        </div>
      </form>
    </Offcanvas>
  );
};

export default AddAdGroupDrawer;
